import { EVENTS } from '../../constants/analytics';
import type { SearchType } from './search-impression-properties';

type TrackFn = (eventName: string, properties?: Record<string, unknown>) => void;

export interface ShowMoreResultsParams {
  term: string;
  searchSessionId?: string;
  contextId?: string;
  searchType: SearchType;
  section: string;
  sectionType: string;
  sectionPosition: number;
  visibleCount: number;
  revealedCount: number;
  remainingCount: number;
}

/**
 * Reporter for the search-v2 group paging events, built on an injected
 * `track` so tests assert on calls without Mixpanel. `showMoreResults` fires
 * `[Search] Show more results` once per press with the group it expanded, which tier
 * (`searchType`: exact `regular` hits or `extended` related resources) and the counts
 * before and after; a press without a search context or term is skipped with a
 * warning, since the event could not join its search session.
 *
 * @param track - The analytics transport (`analyticsService.track`).
 * @returns The reporter.
 * @example
 * const reporter = createSearchPagingReporter(analyticsService.track);
 * reporter.showMoreResults({ term, contextId, searchType: SEARCH_TYPE.REGULAR, section,
 *   sectionType, sectionPosition, visibleCount: 5, revealedCount: 10, remainingCount: 3 });
 */
export const createSearchPagingReporter = (track: TrackFn) => ({
  showMoreResults: ({
    term,
    searchSessionId,
    contextId,
    searchType,
    section,
    sectionType,
    sectionPosition,
    visibleCount,
    revealedCount,
    remainingCount,
  }: ShowMoreResultsParams) => {
    if (!contextId || !term) {
      console.warn('[search-paging-analytics] show more without a search context', {
        term,
        contextId,
      });
      return;
    }
    track(EVENTS.SEARCH.SHOW_MORE_RESULTS, {
      term,
      searchSessionId,
      contextId,
      searchType,
      section,
      sectionType,
      sectionPosition,
      visibleCount,
      revealedCount,
      remainingCount,
    });
  },
});

export type SearchPagingReporter = ReturnType<typeof createSearchPagingReporter>;
